import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { QuestionCircleOutlined, SelectOutlined } from '@ant-design/icons';
import { Button, Col, message, Modal, Row, Spin, Tooltip } from 'antd';
import { connect } from 'react-redux';
import './TsAgreementTransaction.scss';
import {
  translateAgreementTypeClassificator as tAgreementType,
  translateTsFuelGenerationClassificator as tFuelGeneration,
  translateTsFuelTypeClassificator as tFuelType,
} from '../../utils/translateHelpers';
import { goToAccountGOSPreTradingAgreementDetails } from '../../utils/gotoLink';
import { getAgreementData } from '../../reducers/agreementViewReducer';
import * as actions from '../../actions/actionCreators/agreementViewActionCreators';
import { resetMaster as agreementResetMaster } from '../../actions/actionCreators/agreementViewActionCreators';
import { FormActions } from '../../components';
import { Form, Field } from 'react-final-form';
import { FORM_AGREEMENT_TRANSACTION } from '../../constants/formKeys';
import { CheckboxField, NumberField } from '../../atoms';
import FormStateToRedux from '../FormStateToRedux';
import { formTsAgreementTransactionFields } from '../../constants/forms/formTsAgreementTransaction';
import { makeRffValidateFromLegacy } from '../rffLegacyValidateAdapter';
import validateSchema from './ValidateSchema';
import { tsAgreementTransactionAction } from '../../actions/actionCreators/tsAgreementActionCreators';
import {
  getCertificateDetailsViewModal,
  hideCertificateDetailsViewModal,
  showCertificateDetailsViewModal,
} from '../../actions/actionCreators/modalsActionCreators';
import { TsReservationDetailsView } from '../../forms';

export class TsAgreementTransaction extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    assignment: PropTypes.object.isRequired,
    agreement: PropTypes.object.isRequired,
    hideModal: PropTypes.func.isRequired,
    fetchAgreement: PropTypes.func.isRequired,
    tsAgreementTransactionAction: PropTypes.func.isRequired,
    stateCertificateDetailsModal: PropTypes.bool.isRequired,
    showCertificateDetailsModal: PropTypes.func.isRequired,
    isConfirmable: PropTypes.bool.isRequired,
    onSuccess: PropTypes.func,
  };

  state = {
    checkboxState: false,
  };

  componentDidMount = () => {
    const { assignment, fetchAgreement } = this.props;
    fetchAgreement(assignment.sourceId);
  };

  componentWillUnmount() {
    const { resetAgreement } = this.props;
    resetAgreement();
  }

  componentDidUpdate(prevProps) {
    const { agreement } = this.props;
    if (agreement.id !== prevProps.agreement.id) {
      this._form?.change(
        formTsAgreementTransactionFields.PRICE,
        agreement.unitPrice
      );
    }
  }

  onSubmit = async (values) => {
    const { assignment, t, hideModal, onSuccess } = this.props;
    const result = await this.props.tsAgreementTransactionAction(
      assignment.id,
      {
        isAccepted: true,
        price: values.price,
      }
    );
    if (!result?.error) {
      message.success(t('assignments.transaction.performSuccess'));
      hideModal();
      onSuccess?.();
    }
  };

  handleView(id) {
    this.props.hideModal();
    goToAccountGOSPreTradingAgreementDetails(this.props.registerCode, id);
  }

  handleCertificateModal = () => {
    const { showCertificateDetailsModal } = this.props;
    showCertificateDetailsModal();
  };

  renderRow = (label, value) => (
    <div>
      <span>{label}: </span>
      <span>{value}</span>
    </div>
  );

  render() {
    const {
      t,
      agreement,
      assignment,
      hideModal,
      stateCertificateDetailsModal,
      hideCertificateDetailsModal,
      isConfirmable,
    } = this.props;
    const { checkboxState } = this.state;

    const isLoading = Object.keys(agreement).length === 0;
    const completedByUser =
      assignment.completedDate && !assignment.modifiedBySystem;

    if (isLoading) return <Spin />;

    const validate = makeRffValidateFromLegacy(validateSchema, this.props);

    return (
      <Form
        onSubmit={this.onSubmit}
        validate={validate}
        initialValues={{
          [formTsAgreementTransactionFields.PRICE]: agreement.unitPrice,
          ...(assignment.completedDate && {
            [formTsAgreementTransactionFields.CHECKBOX]: true,
          }),
        }}
        render={({ handleSubmit, form }) => {
          this._form = form;
          return (
            <form onSubmit={handleSubmit}>
              <FormStateToRedux form={FORM_AGREEMENT_TRANSACTION} />
              <Row gutter={32}>
                <Col span={24} className="detail-container">
                  <div>
                    <span>{t('assignments.transaction.agreementNr')}: </span>
                    <a onClick={() => this.handleView(agreement.id)}>
                      {agreement.id}
                    </a>
                  </div>
                  {this.renderRow(
                    t('assignments.transaction.counterparty'),
                    agreement.counterpartyName
                  )}
                  {this.renderRow(
                    t('assignments.transaction.createdAt'),
                    agreement.createdAt.split(' ')[0]
                  )}
                  {this.renderRow(
                    t('assignments.transaction.dueDate'),
                    assignment.dueDate
                  )}
                  <br />
                  {this.renderRow(
                    t('assignments.transaction.agreementType'),
                    tAgreementType(agreement.agreementType)
                  )}
                  {this.renderRow(
                    t('assignments.transaction.fuelType'),
                    tFuelType(agreement.fuelType)
                  )}
                  {this.renderRow(
                    t('assignments.transaction.fuelGeneration'),
                    tFuelGeneration(agreement.fuelGeneration)
                  )}
                  {agreement.ghgCapacity &&
                    this.renderRow(
                      <span>
                        {t('agreements.table.ghgCapacity')}&nbsp;
                        <Tooltip title={t('agreements.table.ghgTooltip')}>
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </span>,
                      agreement.ghgCapacity.toFixed(1)
                    )}
                  <br />
                  {this.renderRow(
                    t('assignments.transaction.naturalAmountMj'),
                    agreement.naturalAmountMj.toFixed(1)
                  )}
                  {this.renderRow(
                    t('assignments.transaction.multiplier'),
                    agreement.multiplier
                  )}
                  {this.renderRow(
                    t('assignments.transaction.calculatedAmountMj'),
                    agreement.calculatedAmountMj.toFixed(1)
                  )}
                  {this.renderRow(
                    t('assignments.transaction.fulfilledPercentage'),
                    agreement.agreementType === 'ENTIRE'
                      ? t('assignments.transaction.none')
                      : agreement.fulfilledPercentage.toFixed(2)
                  )}
                  <br />
                  {this.renderRow(
                    t('assignments.transaction.details'),
                    <Button
                      shape="circle"
                      icon={<SelectOutlined />}
                      size="small"
                      type="primary"
                      title={t('assignments.transaction.viewBtn')}
                      onClick={() => this.handleCertificateModal()}
                    />
                  )}
                  <br />
                  <Field
                    min={0}
                    step={0.001}
                    precision={3}
                    name={formTsAgreementTransactionFields.PRICE}
                    label={t('assignments.transaction.price')}
                    component={NumberField}
                    isFloat
                    readOnly={!isConfirmable}
                  />
                  <br />
                </Col>
              </Row>
              {completedByUser && (
                <div className="mb-1">
                  <span>
                    <b>{t('assignments.transaction.completedDate')}: </b>
                  </span>
                  <span>{assignment.completedDate}</span>
                </div>
              )}
              {(isConfirmable || completedByUser) && (
                <Row>
                  <Col className="checkbox-tooltip">
                    <Field
                      name={formTsAgreementTransactionFields.CHECKBOX}
                      type="checkbox"
                      component={CheckboxField}
                      checkboxText={t('assignments.transaction.checkboxInfo')}
                      tooltip={t('assignments.transaction.tooltip')}
                      hasFeedback={false}
                      onChange={(event) =>
                        this.setState({ checkboxState: event.target.checked })
                      }
                      disabled={!isConfirmable}
                    />
                  </Col>
                </Row>
              )}
              {isConfirmable && (
                <FormActions>
                  <Button onClick={hideModal}>
                    {t('assignments.transaction.cancel')}{' '}
                  </Button>
                  <Button
                    disabled={!checkboxState}
                    type="primary"
                    htmlType="submit"
                  >
                    {t('assignments.transaction.confirm')}
                  </Button>
                </FormActions>
              )}
              {stateCertificateDetailsModal && (
                <Modal
                  onCancel={hideCertificateDetailsModal}
                  open
                  footer={false}
                  title={t('assignments.reserveDetails.title')}
                  witdth="600px"
                >
                  <TsReservationDetailsView
                    t={t}
                    assignmentId={assignment.id}
                    hideModal={hideCertificateDetailsModal}
                  />
                </Modal>
              )}
            </form>
          );
        }}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  agreement: getAgreementData(state).toJS(),
  stateCertificateDetailsModal: getCertificateDetailsViewModal(state),
});

const mapDispatchToProps = {
  fetchAgreement: actions.fetchAgreementByPreTransaction,
  showCertificateDetailsModal: showCertificateDetailsViewModal,
  hideCertificateDetailsModal: hideCertificateDetailsViewModal,
  resetAgreement: agreementResetMaster,
  tsAgreementTransactionAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TsAgreementTransaction);
